
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { login } from '../api';
import logo from '../assets/logo.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const LOGIN_MODES = ['user', 'partner', 'admin'];

// Wording for each login tab
const MODE_TEXT = {
  user: {
    tab: 'User Login',
    eyebrow: 'Log In',
    title: 'Access your account',
    intro: 'Enter your credentials to continue. The same login supports regular users and staff accounts.',
    idLabel: 'Email or Login ID',
    idPlaceholder: 'Enter email or user ID',
    button: 'Login'
  },
  partner: {
    tab: 'Partner Login',
    eyebrow: 'Partner Portal',
    title: 'Welcome back, partner',
    intro: 'Sign in with the partner login ID and password given to you by Dani Job Club.',
    idLabel: 'Partner Login ID',
    idPlaceholder: 'Enter your partner login ID',
    button: 'Login to Partner Portal'
  },
  admin: {
    tab: 'Admin Login',
    eyebrow: 'Admin Login',
    title: 'Enter the management console',
    intro: 'Use your admin email and password to access the dashboard securely.',
    idLabel: 'Admin Email',
    idPlaceholder: 'Enter admin email',
    button: 'Login as Admin'
  }
};

const PARTNER_FEATURES = [
  { icon: '📝', title: 'Register members', text: 'Submit candidates with resume, remarks and voice notes.' },
  { icon: '📊', title: 'Track every step', text: 'See HR status and placements as they happen.' },
  { icon: '💰', title: 'Earn commissions', text: 'Get paid for every member placed through you.' },
  { icon: '💬', title: 'Chat with our team', text: 'Message Dani Job Club directly, by text or voice.' }
];

const Login = () => {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loginMode, setLoginMode] = useState('user');
  const navigate = useNavigate();

  const [showForgot, setShowForgot] = useState(false);
  const [forgotForm, setForgotForm] = useState({ user_id: '', contact_name: '', contact_phone: '', message: '' });
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [forgotError, setForgotError] = useState('');

  useEffect(() => {
    const mode = searchParams.get('mode');
    setLoginMode(LOGIN_MODES.includes(mode) ? mode : 'user');
  }, [searchParams]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleModeChange = (mode) => {
    setLoginMode(mode);
    setError('');
    setSuccess(false);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    try {
      if (loginMode === 'admin') {
        const res = await axios.post(`${API_URL}/api/auth/admin-login`, {
          email: form.email,
          password: form.password
        });
        localStorage.setItem('adminToken', res.data.token);
        localStorage.setItem('adminUser', JSON.stringify(res.data.user));
        window.dispatchEvent(new Event('user-login'));
        setSuccess(true);
        setTimeout(() => {
          navigate('/admin');
        }, 1000);
      } else {
        const res = await login(form);
        if (loginMode === 'partner' && res.data.user.type !== 'partner') {
          setError('This is not a partner account. Please use User Login instead.');
          return;
        }
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        window.dispatchEvent(new Event('user-login'));
        setSuccess(true);
        setTimeout(() => {
          if (res.data.user.type === 'admin') navigate('/admin');
          else if (res.data.user.type === 'staff') navigate('/staff');
          else if (res.data.user.type === 'partner') navigate('/partner');
          else navigate('/');
        }, 1000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, forgotForm);
      setForgotSuccess('Request submitted! Admin will contact you to reset your password.');
      setForgotForm({ user_id: '', contact_name: '', contact_phone: '', message: '' });
    } catch (err) {
      setForgotError(err?.response?.data?.error || 'Failed to submit request');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white py-8 md:py-10 font-official">
      <div className="w-[calc(100%-1.5rem)] md:w-[calc(100%-3rem)] xl:w-[calc(100%-12rem)] mx-auto min-h-[calc(100vh-4rem)] rounded-[36px] overflow-hidden border border-yellow-300 shadow-[0_30px_90px_rgba(0,0,0,0.08)] bg-white">
        <div className="grid min-h-[calc(100vh-4rem)] grid-cols-1 xl:grid-cols-[0.95fr_1.05fr]">
          {loginMode === 'partner' ? (
            <div className="relative hidden xl:flex flex-col justify-center overflow-hidden bg-[linear-gradient(160deg,#000_0%,#1a1500_55%,#3d3000_100%)] px-12 py-12">
              <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-yellow-400/20 blur-3xl" />
              <div className="absolute -bottom-32 -left-20 h-80 w-80 rounded-full bg-yellow-500/10 blur-3xl" />
              <div className="relative flex items-center gap-4">
                <img src={logo} alt="Dani Job Club" className="w-20 drop-shadow-2xl" />
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-[0.35em] text-yellow-400">Dani Job Club</p>
                  <p className="text-3xl font-black text-white">Partner Portal</p>
                </div>
              </div>
              <p className="relative mt-6 max-w-md text-lg leading-8 text-gray-300">
                Grow together with Kerala&apos;s online recruitment partner. Everything you need to manage your referrals, in one place.
              </p>
              <div className="relative mt-8 grid gap-3">
                {PARTNER_FEATURES.map((f) => (
                  <div key={f.title} className="flex items-start gap-4 rounded-2xl border border-yellow-400/20 bg-white/5 px-4 py-3 backdrop-blur">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-yellow-400 text-xl">{f.icon}</span>
                    <div>
                      <p className="font-extrabold text-yellow-300">{f.title}</p>
                      <p className="text-sm text-gray-300">{f.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="relative mt-8 text-xs font-bold uppercase tracking-[0.3em] text-yellow-500/70">🤝 Together since 2020</p>
            </div>
          ) : (
            <div className="relative hidden xl:flex items-center justify-center overflow-hidden bg-black">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,214,10,0.12),transparent_70%)]" />
              <img src={logo} alt="Dani Job Club" className="relative w-80 drop-shadow-2xl" />
            </div>
          )}

          <div className="flex items-center justify-center bg-[linear-gradient(180deg,#fff_0%,#fff9dc_100%)] px-5 py-8 md:px-8 xl:px-12">
            <div className="w-full max-w-xl">
              <div className="flex flex-wrap gap-2">
                {LOGIN_MODES.map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => handleModeChange(mode)}
                    className={`rounded-full px-4 py-2 text-xs font-extrabold uppercase tracking-[0.2em] transition ${
                      loginMode === mode
                        ? mode === 'partner' ? 'bg-yellow-400 text-black shadow-md' : 'bg-black text-yellow-300'
                        : 'border border-gray-300 bg-white text-gray-700 hover:border-yellow-400'
                    }`}
                  >
                    {mode === 'partner' && '🤝 '}{MODE_TEXT[mode].tab}
                  </button>
                ))}
              </div>

              {loginMode === 'partner' && (
                <div className="mt-5 flex items-center gap-3 rounded-2xl bg-black px-4 py-3 xl:hidden">
                  <img src={logo} alt="" className="w-10" />
                  <div>
                    <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-yellow-400">Dani Job Club</p>
                    <p className="text-lg font-black text-white">Partner Portal</p>
                  </div>
                </div>
              )}
              <p className="mt-5 text-sm font-extrabold uppercase tracking-[0.3em] text-yellow-600">
                {MODE_TEXT[loginMode].eyebrow}
              </p>
              <h2 className="mt-3 text-4xl md:text-5xl font-extrabold leading-tight text-black">
                {MODE_TEXT[loginMode].title}
              </h2>
              <p className="mt-3 text-gray-600 text-base leading-7">
                {MODE_TEXT[loginMode].intro}
              </p>

              <div className={`mt-8 rounded-[30px] border bg-white px-6 py-6 md:px-7 shadow-[0_22px_65px_rgba(0,0,0,0.07)] ${loginMode === 'partner' ? 'border-yellow-400 ring-4 ring-yellow-100' : 'border-gray-200'}`}>
                <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
                  <label className="block">
                    <span className="text-sm font-bold text-black">{MODE_TEXT[loginMode].idLabel}</span>
                    <input name="email" type={loginMode === 'admin' ? 'email' : 'text'} placeholder={MODE_TEXT[loginMode].idPlaceholder} className="mt-2 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200" value={form.email} onChange={handleChange} required />
                  </label>
                  <label className="block">
                    <span className="text-sm font-bold text-black">Password</span>
                    <input name="password" type="password" placeholder="Enter password" className="mt-2 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200" value={form.password} onChange={handleChange} required />
                  </label>
                  <button type="submit" className={`rounded-2xl px-4 py-3 font-extrabold transition ${loginMode === 'partner' ? 'bg-black text-yellow-400 hover:bg-gray-900' : 'bg-yellow-400 text-black hover:bg-yellow-300'}`}>
                    {MODE_TEXT[loginMode].button}
                  </button>
                </form>
                {error && <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-600">{error}</div>}
                {success && <div className="mt-4 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-green-700">Login successful! Redirecting...</div>}
                {loginMode === 'user' && (
                  <div className="mt-6 flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-gray-600">New here? Create your account.</span>
                    <Link to="/register" className="inline-flex items-center justify-center rounded-xl border border-black px-4 py-2 font-bold text-black hover:border-yellow-500 hover:text-yellow-700 transition">Register</Link>
                  </div>
                )}
                {loginMode === 'partner' && (
                  <div className="mt-6 flex flex-col gap-3 rounded-2xl bg-yellow-50 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-gray-700">Not a partner yet? Join our network.</span>
                    <Link to="/partnership" className="inline-flex items-center justify-center rounded-xl bg-yellow-400 px-4 py-2 font-bold text-black hover:bg-yellow-300 transition">Apply for Partnership</Link>
                  </div>
                )}

                {loginMode !== 'admin' && (
                  <div className="mt-4">
                    <button type="button" onClick={() => { setShowForgot((v) => !v); setForgotSuccess(''); setForgotError(''); }}
                      className="text-xs font-semibold text-gray-500 underline underline-offset-2 hover:text-blue-600">
                      {showForgot ? 'Hide' : 'Forgot password?'}
                    </button>
                    {showForgot && (
                      <form onSubmit={handleForgotSubmit} className="mt-3 rounded-2xl border border-blue-100 bg-blue-50 p-4 flex flex-col gap-3">
                        <p className="text-xs font-bold text-gray-700">Enter your login details and contact info. Admin will reset your password and reach out.</p>
                        <label className="text-xs font-semibold text-gray-700">Your User ID (login ID)
                          <input type="text" value={forgotForm.user_id}
                            onChange={(e) => setForgotForm((p) => ({ ...p, user_id: e.target.value }))}
                            placeholder={loginMode === 'partner' ? 'Your partner login ID' : 'e.g. your email or user ID'}
                            className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm" required />
                        </label>
                        <label className="text-xs font-semibold text-gray-700">Your Name
                          <input type="text" value={forgotForm.contact_name}
                            onChange={(e) => setForgotForm((p) => ({ ...p, contact_name: e.target.value }))}
                            className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm" />
                        </label>
                        <label className="text-xs font-semibold text-gray-700">WhatsApp / Phone
                          <input type="text" value={forgotForm.contact_phone}
                            onChange={(e) => setForgotForm((p) => ({ ...p, contact_phone: e.target.value }))}
                            className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm" />
                        </label>
                        <label className="text-xs font-semibold text-gray-700">Message (optional)
                          <input type="text" value={forgotForm.message}
                            onChange={(e) => setForgotForm((p) => ({ ...p, message: e.target.value }))}
                            placeholder="Any additional info"
                            className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm" />
                        </label>
                        {forgotError && <p className="text-xs font-bold text-red-600">{forgotError}</p>}
                        {forgotSuccess && <p className="text-xs font-bold text-green-700">{forgotSuccess}</p>}
                        <button type="submit" disabled={forgotLoading}
                          className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-extrabold text-white hover:bg-blue-700 disabled:opacity-60">
                          {forgotLoading ? 'Submitting...' : 'Submit Reset Request'}
                        </button>
                      </form>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
